import DateTimePicker, {
  type DateTimePickerChangeEvent,
} from "@react-native-community/datetimepicker";
import { useTranslation } from "react-i18next";
import {
  Pressable,
  StyleSheet,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";

import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";

type IOSPickerSheetProps = {
  visible: boolean;
  value: Date;
  /** `time` for the reminder wheel; everything else picks a date. */
  mode?: "date" | "time";
  onValueChange: (event: DateTimePickerChangeEvent, next: Date) => void;
  onClose: () => void;
  minimumDate?: Date;
  maximumDate?: Date;
  /** Announced as the sheet's purpose, e.g. "Choose your date of birth". */
  accessibilityLabel: string;
};

/**
 * The iOS picker wheel, with a way out of it.
 *
 * On iOS the community picker has no chrome of its own — no toolbar, no Done,
 * no dismissal. Rendered inline under a field, as all FOUR wheels in this app
 * used to be, the ONLY way to put one away was to tap its trigger again, and
 * nothing on screen said so: the wheel simply sat there under the form with the
 * Continue button pushed off the bottom. Tapping outside it did nothing.
 * Android is unaffected — `DateTimePickerAndroid.open` is a real dialog with
 * its own buttons — which is why this went unnoticed for so long.
 *
 * Shared rather than fixed in place, because it was never one screen's bug: the
 * date of birth, the reflection date, the goal due date and the reminder time
 * were four copies of the same inline wheel, and a Done button added to the one
 * that got reported would have left the other three exactly as they were.
 *
 * So the wheel moves into a sheet that owns the two dismissals iOS users
 * already expect: the backdrop, and an explicit Done. Both merely close. There
 * is no Cancel, and that is deliberate rather than an omission — the wheel
 * commits as it spins, exactly as it did inline, so the value on screen is
 * always the value that is set. A Cancel would have to restore a prior value
 * and would make dismissing-by-backdrop ambiguous: on iOS a backdrop tap means
 * "I'm finished", not "undo".
 *
 * A `Modal`, not an absolutely-positioned overlay, because these live inside
 * scrolling forms — an overlay would be clipped by the `ScrollView` and would
 * scroll away from the wheel it belongs to.
 */
export function IOSPickerSheet({
  visible,
  value,
  mode = "date",
  onValueChange,
  onClose,
  minimumDate,
  maximumDate,
  accessibilityLabel,
}: IOSPickerSheetProps) {
  const c = useTheme();
  const { t } = useTranslation();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      statusBarTranslucent
      // The hardware/gesture dismissal, which must agree with the other two.
      onRequestClose={onClose}
    >
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={t("common.dismiss")}
          onPress={onClose}
        />

        <View
          style={[
            styles.sheet,
            { backgroundColor: c.surface, borderColor: c.border },
          ]}
          accessibilityLabel={accessibilityLabel}
        >
          <View style={[styles.toolbar, { borderBottomColor: c.border }]}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("common.done")}
              onPress={onClose}
              hitSlop={10}
              style={({ pressed }) => [styles.done, pressed && styles.pressed]}
            >
              <ThemedText variant="bodyStrong" style={{ color: c.tint }}>
                {t("common.done")}
              </ThemedText>
            </Pressable>
          </View>

          <DateTimePicker
            value={value}
            mode={mode}
            display="spinner"
            minimumDate={minimumDate}
            maximumDate={maximumDate}
            onValueChange={onValueChange}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    justifyContent: "flex-end",
  },
  sheet: {
    borderTopWidth: 1,
    borderTopLeftRadius: Radius.lg,
    borderTopRightRadius: Radius.lg,
    paddingBottom: Spacing.lg,
  },
  toolbar: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  done: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.xs,
  },
  pressed: { opacity: 0.6 },
});
